import React, { useEffect, useState } from "react";
import "./mobileappprompt.css";
/**
 * MobileAppPrompt
 *
 * Props:
 *  - androidPackage (string) e.g. "com.mycompany.myapp"
 *  - iosAppId (string) e.g. "123456789" (App Store numeric id)
 *  - deepLink (string) e.g. "myapp://home"
 *  - minWidth (number) viewport width threshold; default 900
 *  - dismissDurationDays (number) how long to remember user dismissed in days (default 30)
 */
const DISMISS_KEY = "mobile_app_prompt_dismissed_at";

const MobileAppPrompt = ({
  androidPackage = "YOUR_ANDROID_PACKAGE_NAME",
  iosAppId = "YOUR_IOS_APP_ID",
  deepLink = "yourappscheme://",
  minWidth = 900,
  dismissDurationDays = 30,
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [checking, setChecking] = useState(true);

  // localStorage dismissal check
  const isDismissed = () => {
    try {
      const v = localStorage.getItem(DISMISS_KEY);
      if (!v) return false;
      const t = parseInt(v, 10);
      if (Number.isNaN(t)) return false;
      const ageMs = Date.now() - t;
      return ageMs < dismissDurationDays * 24 * 60 * 60 * 1000;
    } catch (e) {
      return false;
    }
  };

  // lock/unlock body scroll when modal visible
  useEffect(() => {
    if (showPrompt) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => document.body.classList.remove("no-scroll");
  }, [showPrompt]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // skip if dismissed or not mobile
    if (isDismissed()) {
      setChecking(false);
      return;
    }

    const isMobile = window.innerWidth < minWidth;
    if (!isMobile) {
      setChecking(false);
      return;
    }

    // 1) navigator.getInstalledRelatedApps()
    const checkInstalledRelatedApps = async () => {
      if (navigator.getInstalledRelatedApps) {
        try {
          const related = await navigator.getInstalledRelatedApps();
          const found = related.some((app) => {
            if (
              app.platform &&
              app.platform.includes("play") &&
              androidPackage
            ) {
              return app.id === androidPackage;
            }
            if (app.platform && app.platform.includes("itunes") && iosAppId) {
              return String(app.id) === String(iosAppId);
            }
            return false;
          });

          if (found) {
            redirectToApp();
            return true;
          }
        } catch (e) {
          // ignore
        }
      }
      return false;
    };

    // 2) Deep link attempt using iframe + visibilitychange detection
    const tryOpenDeepLink = (url, fallbackTimeout = 1200) =>
      new Promise((resolve) => {
        let resolved = false;

        const onVisibilityChange = () => {
          if (document.hidden && !resolved) {
            resolved = true;
            cleanup();
            resolve(true);
          }
        };

        const cleanup = () => {
          document.removeEventListener("visibilitychange", onVisibilityChange);
          window.removeEventListener("pagehide", onVisibilityChange);
          clearTimeout(timer);
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("pagehide", onVisibilityChange);

        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = url;
        document.body.appendChild(iframe);

        const timer = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            cleanup();
            try {
              document.body.removeChild(iframe);
            } catch (e) {}
            resolve(false);
          }
        }, fallbackTimeout);
      });

    const redirectToApp = () => {
      try {
        // prefer deep link
        window.location.href = deepLink;
      } catch (e) {
        // ignore
      }
    };

    const mainCheck = async () => {
      setChecking(true);

      const found = await checkInstalledRelatedApps();
      if (found) return;

      try {
        const opened = await tryOpenDeepLink(deepLink, 1100);
        if (opened) return;
      } catch (e) {
        // ignore
      }

      // not found -> show prompt
      setShowPrompt(true);
      setChecking(false);
    };

    mainCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [androidPackage, iosAppId, deepLink, minWidth, dismissDurationDays]);

  if (!showPrompt) return null;

  const storeLinks = {
    play: `https://play.google.com/store/apps/details?id=${encodeURIComponent(
      androidPackage
    )}`,
    appstore: `https://apps.apple.com/app/id${encodeURIComponent(iosAppId)}`,
  };

  const dismiss = (remember = true) => {
    setShowPrompt(false);
    try {
      if (remember) localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {}
  };

  return (
    <div
      className="mobile-app-prompt-overlay fade-in"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="mobile-app-prompt-card enhanced-popup"
        aria-labelledby="mobile-prompt-title"
      >
        {/* App Grid SVG */}
        <div className="popup-icon" aria-hidden>
          <svg
            width="42"
            height="42"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2563eb"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="4" ry="4"></rect>
            <circle cx="8" cy="8" r="1.3"></circle>
            <circle cx="12" cy="8" r="1.3"></circle>
            <circle cx="16" cy="8" r="1.3"></circle>
            <circle cx="8" cy="12" r="1.3"></circle>
            <circle cx="12" cy="12" r="1.3"></circle>
            <circle cx="16" cy="12" r="1.3"></circle>
            <circle cx="8" cy="16" r="1.3"></circle>
            <circle cx="12" cy="16" r="1.3"></circle>
            <circle cx="16" cy="16" r="1.3"></circle>
          </svg>
        </div>

        <h2 id="mobile-prompt-title" className="popup-title">
          Mobile App Recommended
        </h2>

        <p className="popup-subtitle">
          For the best experience on mobile, please use our official app.
        </p>

        <div className="popup-buttons">
          <a
            className="popup-btn android"
            href={storeLinks.play}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download for Android"
          >
            Download for Android
          </a>

          <a
            className="popup-btn ios"
            href={storeLinks.appstore}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download for iOS"
          >
            Download for iOS
          </a>
        </div>

        <button className="continue-btn" onClick={() => dismiss(false)}>
          Continue in browser
        </button>
      </div>
    </div>
  );
};

export default MobileAppPrompt;
